import { cn } from "@base/lib/utils";
import { Image } from "@base/ui/Image";
import { ErrorBoundary } from "@sentry/react";
import { useParams } from "@tanstack/react-router";
import { ImageOff } from "lucide-react";
import { Suspense } from "react";
import { useProjectDetailQuery } from "../model/useProjectDetailQuery";

function ProjectHeroImageSkeleton() {
  return <div className="my-4 w-full aspect-[32/15] md:aspect-[16/5] rounded-8 skeleton-shimmer" />;
}

function ProjectHeroImageError() {
  return (
    <div className="my-4 w-full aspect-[32/15] md:aspect-[16/5] rounded-8 bg-gray-100 flex items-center justify-center" />
  );
}

function ProjectHeroImageContent() {
  const { projectId } = useParams({ from: "/_authenticated/projects/$projectId/" });
  const { data: project } = useProjectDetailQuery(projectId);
  const hasImage = Boolean(project.thumbnail_image_url);

  return (
    <div className="my-4 relative">
      {hasImage ? (
        <Image
          src={project.thumbnail_image_url}
          alt=""
          className="w-full object-cover aspect-[32/15] md:aspect-[16/5] rounded-8"
        />
      ) : (
        <div className="w-full aspect-[32/15] md:aspect-[16/5] rounded-8 bg-gray-100 flex items-center justify-center">
          <ImageOff className="w-8 h-8 text-gray-300" />
        </div>
      )}

      <div className="flex absolute inset-0 justify-center items-center bg-white/50 rounded-8 md:hidden">
        <p className="text-title6 text-gray-1000">{project.name}</p>
      </div>

      <div
        className={cn(
          "hidden absolute inset-0 flex-col justify-end p-6 rounded-8 md:flex",
          hasImage && "bg-gradient-to-t from-black/70 via-black/10 to-transparent",
        )}
      >
        <p className={cn("truncate text-title4", hasImage ? "text-white" : "text-gray-1000")} title={project.name}>
          {project.name}
        </p>
        <p className={cn("mt-1 truncate text-body3", hasImage ? "text-white/85" : "text-gray-700")}>
          {project.address}
        </p>
      </div>
    </div>
  );
}

export function ProjectHeroImage() {
  return (
    <ErrorBoundary fallback={<ProjectHeroImageError />}>
      <Suspense fallback={<ProjectHeroImageSkeleton />}>
        <ProjectHeroImageContent />
      </Suspense>
    </ErrorBoundary>
  );
}
